import React from 'react'
import { useFormContext, Controller } from 'react-hook-form'
import Box from '@material-ui/core/Box'
import Container from '@material-ui/core/Container'
import Typography from '@material-ui/core/Typography'
import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import Button from '@material-ui/core/Button'
import AccountRouterLink from 'components/account-router-link'
import { useWizardFormContext } from 'components/wizard-form/context'
import CurrencyField from 'components/form-util/currency'
import InfoBox from 'components/info-box'
import { flags, useFeatureFlags } from 'lib/feature-flags'
import { makeStyles } from '@material-ui/core/styles'
import FormHelperText from '@material-ui/core/FormHelperText'

const useStyles = makeStyles(theme => ({
  divider: {
    borderTop: `1px solid ${theme.palette.grey[300]}`,
    borderBottom: '0',
  },
}))

const PaymentAccountRedesign = () => {
  const classes = useStyles()
  const [isRecurringPaymentScheduleEnabled] = useFeatureFlags([
    flags.FLAG_FEAT_GROUP_EVENT_RECURRING_PAYMENTS_ENABLED,
  ])

  const { errors, control, watch, register, setValue } = useFormContext()
  const {
    extraData: { paymentAccounts },
  } = useWizardFormContext()

  const isExternalRegistration = watch('isExternalRegistration')
  const feeText =
    'Please note that we use Stripe to process credit card transactions and that it deducts a fee of 2.9% + $0.30 from the transaction total.'

  const paymentRecurrenceSchedule = watch('paymentRecurrenceSchedule')
  const sessions = watch('sessions')

  const resolveStartDateRange = () => {
    if (!sessions) return []

    let earliestStartDate = null
    let latestStartDate = null

    for (const session of sessions) {
      if (!earliestStartDate) {
        earliestStartDate = session.startDate
      } else if (session.startDate < earliestStartDate) {
        earliestStartDate = session.startDate
      }

      if (!latestStartDate) {
        latestStartDate = session.startDate
      } else if (session.startDate > latestStartDate) {
        latestStartDate = session.startDate
      }
    }

    return [earliestStartDate, latestStartDate]
  }

  const shouldShowWeekly = () => {
    if (!isRecurringPaymentScheduleEnabled) return false
    if (!sessions) return false
    if (sessions.length <= 1) return false

    const [earliestStartDate, latestStartDate] = resolveStartDateRange()

    if (!earliestStartDate || !latestStartDate) return false

    // Return false if days between start and end is less than 7
    const weeksBetween = latestStartDate.diff(earliestStartDate, 'week').weeks
    if (weeksBetween < 1) return false

    return true
  }

  const shouldShowMonthly = () => {
    if (!isRecurringPaymentScheduleEnabled) return false
    if (!sessions) return false
    if (sessions.length <= 1) return false

    const [earliestStartDate, latestStartDate] = resolveStartDateRange()

    if (!earliestStartDate || !latestStartDate) return false

    // Return false if days between start and end is less than a month
    const monthsBetween = latestStartDate.diff(earliestStartDate, 'month')
      .months
    if (monthsBetween < 1) return false

    return true
  }

  if (isExternalRegistration && paymentRecurrenceSchedule !== 'none') {
    setValue('paymentRecurrenceSchedule', 'none')
  }

  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      <Box mb={6}>
        <Typography variant="h4" align="center">
          How do you want to collect payment for this offering?
        </Typography>
      </Box>
      <Container maxWidth="xs">
        <Box mb={6}>
          <FormControl
            error={!!errors.paymentRecurrenceSchedule}
            variant="outlined"
            required
          >
            <InputLabel id="payment-recurrence-schedule-label">
              Payment Collection
            </InputLabel>
            <Controller
              defaultValue=""
              as={
                <Select
                  labelId="paymentRecurrenceSchedule"
                  label="Payment Collection"
                  disabled={isExternalRegistration}
                  defaultValue=""
                  data-cy="payment-recurrence-schedule"
                >
                  <MenuItem key="once" value="once">
                    One-Time Payment
                  </MenuItem>
                  {shouldShowWeekly() && (
                    <MenuItem key="week" value="week">
                      Weekly Payments
                    </MenuItem>
                  )}
                  {shouldShowMonthly() && (
                    <MenuItem key="month" value="month">
                      Monthly Payments
                    </MenuItem>
                  )}
                  <MenuItem key="none" value="none">
                    Manually Collect Payment
                  </MenuItem>
                </Select>
              }
              name="paymentRecurrenceSchedule"
              control={control}
            />
            {/* TODO: Is there a way to show the error with the above components already? */}
            {errors?.paymentRecurrenceSchedule && (
              <FormHelperText error={true}>
                {errors.paymentRecurrenceSchedule.message}
              </FormHelperText>
            )}
          </FormControl>
        </Box>
      </Container>
      <Box mb={2}>
        <Typography variant="h6" align="center">
          How much do you want to charge for this offering{' '}
          {(() => {
            switch (paymentRecurrenceSchedule) {
              case 'week':
                return '(per week)'
              case 'month':
                return '(per month)'
              default:
                return '(all sessions included)'
            }
          })()}
          ?
        </Typography>
      </Box>
      <Container maxWidth="xs">
        <CurrencyField
          name="price"
          label="Price per Person"
          fullWidth
          inputRef={register}
          error={!!errors.price}
          helperText={errors?.price?.message}
        />
      </Container>
      {['once', 'week', 'month'].includes(paymentRecurrenceSchedule) && (
        <>
          <Container maxWidth="md">
            <Box my={6}>
              <hr className={classes.divider} />
            </Box>
          </Container>
          <Container maxWidth="sm">
            <Box mb={1}>
              <Typography variant="h5" align="center">
                Select a payment account
              </Typography>
            </Box>
            <Box mb={4}>
              <Typography variant="body1" align="center">
                Select a payment account to collect registration fees online for
                this offering.
              </Typography>
            </Box>
          </Container>
          <Container maxWidth="xs">
            <Box mb={1}>
              <FormControl
                error={!!errors.paymentAccountId}
                variant="outlined"
                required
              >
                <InputLabel id="category">Payment Account</InputLabel>
                <Controller
                  defaultValue=""
                  name="paymentAccountId"
                  control={control}
                  as={
                    <Select
                      labelId="paymentAccount"
                      label="Select paymentAccount"
                      value=""
                      defaultValue=""
                      data-cy="payment-account"
                    >
                      {paymentAccounts.map(opt => (
                        <MenuItem key={opt.id} value={opt.id}>
                          {opt.name} – x{opt.last4}
                        </MenuItem>
                      ))}
                    </Select>
                  }
                />
              </FormControl>
              <Button
                color="primary"
                size="small"
                to="/account/payments/payment-accounts"
                component={AccountRouterLink}
              >
                Manage Payment Accounts
              </Button>
            </Box>
          </Container>
          <InfoBox text={feeText} maxWidth="sm" />
        </>
      )}
    </Box>
  )
}

export default PaymentAccountRedesign
